// components/Survey/SurveyCategoriesList.jsx
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SurveyCategoryForm from './SurveyCategoryForm';
import { surveyCategoryService } from '../../services/surveyCategoryService';
import { surveyService } from '../../services/surveyService';
import { useAuth } from '../../contexts/AuthContext';
import { PencilIcon, TrashIcon, EyeIcon, EllipsisVerticalIcon, FolderIcon, PlusIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import DataTable from 'react-data-table-component';

const SurveyCategoriesList = () => {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin, isAuthenticated } = useAuth();
  const [categories, setCategories] = useState([]);
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [editingCategory, setEditingCategory] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [actionDropdown, setActionDropdown] = useState(null);
  const dropdownRefs = useRef({});

  useEffect(() => { 
    console.log('🔍 useEffect triggered - isAuthenticated:', isAuthenticated, 'surveyId:', surveyId);
    if (isAuthenticated && surveyId) {
      loadSurvey();
      loadCategories();
    } else {
      console.log('❌ Missing requirements:', { isAuthenticated, surveyId });
      setLoading(false);
    }
  }, [isAuthenticated, surveyId]);

  const loadSurvey = async () => {
    try {
      console.log('📋 Loading survey details for ID:', surveyId);
      const surveyData = await surveyService.getSurvey(surveyId);
      console.log('✅ Survey loaded:', surveyData);
      setSurvey(surveyData);
    } catch (err) {
      console.error('❌ Error loading survey:', err);
      console.error('Error details:', err.response || err.message);
      setError('Failed to load survey details: ' + (err.message || 'Unknown error'));
    }
  };

  const loadCategories = async () => {
    try {
      console.log('🔄 Starting to load categories for survey:', surveyId);
      setLoading(true); 
      setError('');
      
      // Validate surveyId
      if (!surveyId || surveyId === 'undefined') {
        throw new Error('Invalid survey ID: ' + surveyId);
      }
      
      const data = await surveyCategoryService.getSurveyCategories(surveyId);
      console.log('✅ Categories loaded successfully:', data);
      setCategories(data || []);
    } catch (err) {
      console.error('❌ Error loading categories:', err);
      console.error('Error details:', err.response || err.message);
      console.error('Error stack:', err.stack);
      
      const errorMessage = err.response?.data?.message || err.message || 'Unknown error occurred';
      setError('Failed to load categories: ' + errorMessage);
      setCategories([]); // Ensure categories is empty array on error
    } finally { 
      console.log('🏁 Loading complete, setting loading to false');
      setLoading(false); 
    }
  };

  // Add this debug effect to monitor state changes
  useEffect(() => {
    console.log('📊 State update:', {
      loading,
      categoriesCount: categories.length,
      error,
      surveyId
    });
  }, [loading, categories, error, surveyId]);

  const handleDelete = async (id) => {
    if(!window.confirm('Are you sure you want to delete this category?')) return;
    try {
      await surveyCategoryService.deleteSurveyCategory(id);
      await loadCategories();
      setActionDropdown(null);
    } catch (err) {
      console.error(err);
      setError('Failed to delete category: ' + (err.message || 'Unknown error'));
    }
  };

  const handleEdit = (category) => { 
    setEditingCategory(category); 
    setShowForm(true);
    setActionDropdown(null);
  };

  const handleView = (category) => {
    const details = [
      `Category Name: ${category.name}`,
      `Description: ${category.description || 'No description'}`,
      `Order: ${category.order}`,
      `Questions: ${category.questions?.length || 0}`,
      `Survey: ${survey?.name || 'Unknown'}`
    ].join('\n');
    alert(details);
    setActionDropdown(null);
  };

  const handleManageQuestions = (category) => {
    navigate(`/surveys/${surveyId}/categories/${category.id}/questions`);
    setActionDropdown(null);
  };

  const handleActionClick = (rowId, e) => {
    e.stopPropagation();
    setActionDropdown(actionDropdown === rowId ? null : rowId);
  };

  // Filter categories based on search
  const filteredCategories = useMemo(() => {
    if (!search.trim()) return categories;
    
    return categories.filter(category =>
      category.name?.toLowerCase().includes(search.toLowerCase()) ||
      category.description?.toLowerCase().includes(search.toLowerCase())
    );
  }, [categories, search]);

  // Define columns for DataTable
  const columns = [
    {
      name: 'CATEGORY NAME',
      selector: row => row.name,
      sortable: true,
      cell: row => (
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm">
            <FolderIcon className="w-5 h-5" />
          </div>
          <div>
            <div className="font-semibold text-gray-900">{row.name}</div>
            <div className="text-xs text-gray-500 mt-1">
              {row.description || 'No description'} • Order: {row.order}
            </div>
          </div>
        </div>
      ),
      width: '350px'
    },
    {
      name: 'ORDER',
      selector: row => row.order,
      sortable: true,
      cell: row => (
        <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
          {row.order}
        </span>
      ),
      width: '100px'
    },
    {
      name: 'QUESTIONS',
      selector: row => row.questions?.length || 0,
      sortable: true,
      cell: row => (
        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
          {row.questions?.length || 0}
        </span>
      ),
      width: '120px'
    },
    {
      name: 'ACTIONS',
      cell: row => (
        <div 
          ref={el => dropdownRefs.current[row.id] = el}
          className="relative action-dropdown-container"
        >
          <button 
            onClick={(e) => handleActionClick(row.id, e)}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <EllipsisVerticalIcon className="w-5 h-5" />
          </button>
          
          {actionDropdown === rowId && (
            <div className="absolute right-0 mt-1 z-50 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 transform -translate-y-1">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleView(row);
                }}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
              >
                <EyeIcon className="w-4 h-4 mr-3 text-blue-600" />
                View Details
              </button>
              
              {isSuperAdmin && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEdit(row);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                  >
                    <PencilIcon className="w-4 h-4 mr-3 text-green-600" />
                    Edit Category
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleManageQuestions(row);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-150"
                  >
                    <PlusIcon className="w-4 h-4 mr-3 text-purple-600" />
                    Manage Questions
                  </button>
                  
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(row.id);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-150"
                  >
                    <TrashIcon className="w-4 h-4 mr-3" />
                    Delete Category
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      ),
      width: '80px'
    }
  ];

  // Custom styles for DataTable
  const customStyles = {
    table: {
      style: {
        backgroundColor: '#ffffff',
      },
    },
    head: {
      style: {
        fontSize: '0.75rem',
        fontWeight: '700',
        color: '#374151',
        backgroundColor: '#f0f9ff',
      },
    },
    headRow: {
      style: {
        borderTopWidth: '1px',
        borderTopColor: '#e5e7eb',
        borderBottomWidth: '1px',
        borderBottomColor: '#e5e7eb',
        backgroundColor: '#f0f9ff',
      },
    },
    headCells: {
      style: {
        paddingLeft: '1rem',
        paddingRight: '1rem',
        paddingTop: '0.75rem',
        paddingBottom: '0.75rem',
        fontWeight: '600',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        fontSize: '0.75rem',
        backgroundColor: '#f0f9ff',
      },
    },
    cells: {
      style: {
        paddingLeft: '1rem',
        paddingRight: '1rem',
        paddingTop: '1rem',
        paddingBottom: '1rem',
        backgroundColor: '#ffffff',
      },
    },
    rows: {
      style: {
        backgroundColor: '#ffffff',
        borderBottomColor: '#e5e7eb',
        '&:not(:last-of-type)': {
          borderBottomWidth: '1px',
          borderBottomColor: '#e5e7eb',
        },
        '&:hover': {
          backgroundColor: '#f0f9ff',
        },
      },
    },
    pagination: {
      style: {
        backgroundColor: '#ffffff',
        borderTopWidth: '1px',
        borderTopColor: '#e5e7eb',
        paddingTop: '1rem',
        paddingBottom: '1rem',
      },
    },
  };

  // Custom loading component
  const LoadingComponent = () => (
    <div className="flex justify-center items-center py-12 bg-white">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      <span className="ml-3 text-gray-600">Loading categories...</span>
    </div>
  );

  // Custom no data component
  const NoDataComponent = () => (
    <div className="flex flex-col items-center justify-center py-12 text-center bg-white">
      <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
        <FolderIcon className="w-8 h-8 text-blue-500" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">No categories found</h3>
      <p className="text-gray-500 mb-4">
        {search ? 'Try adjusting your search terms' : 'Get started by creating your first category'}
      </p>
      {!search && isSuperAdmin && (
        <button 
          onClick={() => { setEditingCategory(null); setShowForm(true); }}
          className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium"
        >
          Create Category
        </button>
      )}
    </div>
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      let clickedInsideDropdown = false;
      
      Object.values(dropdownRefs.current).forEach(ref => {
        if (ref && ref.contains(event.target)) {
          clickedInsideDropdown = true;
        }
      });

      if (!clickedInsideDropdown) {
        setActionDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (!isAuthenticated) {
    return (
      <div className="p-6 w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Please log in to view categories</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full bg-gradient-to-br from-blue-50 via-white to-blue-50 min-h-screen">
      {/* Header Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <button 
                onClick={() => navigate('/surveys')}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Survey Categories</h1>
                <p className="text-gray-600 mt-1">
                  {survey ? `Managing categories for: ${survey.name}` : 'Loading survey...'}
                </p>
              </div>
            </div>
            {survey && (
              <div className="text-sm text-blue-600">
                Survey: {survey.name} • Company: {survey.company?.name} • Status: {survey.isActive ? 'Active' : 'Inactive'}
              </div>
            )}
          </div>
          {isSuperAdmin && (
            <button 
              onClick={() => { setEditingCategory(null); setShowForm(true); }}
              className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium shadow-sm hover:shadow-md transition-all duration-200"
            >
              Create Category
            </button>
          )}
        </div>
      </div>

      {/* Form Card */}
      {showForm && (
        <div className="mb-6">
          <SurveyCategoryForm
            surveyId={parseInt(surveyId)}
            initialData={editingCategory}
            isEditing={!!editingCategory}
            onSubmit={async (data) => {
              if(editingCategory) await surveyCategoryService.updateSurveyCategory(editingCategory.id, data);
              else await surveyCategoryService.createSurveyCategory(data);
              setShowForm(false); 
              await loadCategories();
            }}
            onCancel={() => setShowForm(false)}
          />
        </div>
      )}

      {/* Search and Stats Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 p-6 mb-6">
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center space-y-4 lg:space-y-0">
          <div className="flex-1 w-full lg:max-w-md">
            <div className="relative">
              <input 
                type="text" 
                placeholder="Search categories by name or description..." 
                value={search} 
                onChange={e => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full font-medium">
              Total: {categories.length}
            </span>
            <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full font-medium">
              Questions: {categories.reduce((acc, cat) => acc + (cat.questions?.length || 0), 0)}
            </span>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl p-4">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Data Table Card */}
      <div className="bg-white rounded-2xl shadow-sm border border-blue-100 overflow-hidden">
        <DataTable
          columns={columns}
          data={filteredCategories}
          progressPending={loading}
          progressComponent={<LoadingComponent />}
          noDataComponent={<NoDataComponent />}
          customStyles={customStyles}
          pagination
          paginationPerPage={10}
          paginationRowsPerPageOptions={[10, 25, 50, 100]}
          highlightOnHover
          pointerOnHover
          responsive
          striped
          persistTableHead
        />
      </div>
    </div>
  );
};

export default SurveyCategoriesList;